/**
 * Scheduling defaults for new work orders (#2465, split out of the 991-line
 * workshop form).
 *
 * Floating vs exact decides whether a booking claims a specific slot; the
 * remaining values are the padding the planner leaves around real work.
 */

import { Box, Paper, Typography, TextField, Grid, ToggleButton, ToggleButtonGroup, FormLabel, FormHelperText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings, SchedulingType, WorkDurationStepMinutes } from '@/types';
import { DEFAULT_WORK_DURATION_STEP_MINUTES } from '@/utils/duration';

const WORK_DURATION_STEP_OPTIONS: readonly WorkDurationStepMinutes[] = [15, 30, 60];

interface Props {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
}

export function WorkshopSectionScheduling({ settings, onChange }: Props) {
  const { t } = useTranslation('settings');

  return (
    <>
      {/* Scheduling */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('scheduling.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('scheduling.lookaheadDesc')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        {/* Scheduling type pre-selected for a new work order (#1931). */}
        <Box sx={{ mb: 3 }}>
          <FormLabel component="legend" sx={{ display: 'block', mb: 1 }}>
            {t('scheduling.defaultSchedulingType')}
          </FormLabel>
          <ToggleButtonGroup
            value={settings.defaultSchedulingType}
            exclusive
            onChange={(_e, value: SchedulingType | null) => {
              if (!value || value === settings.defaultSchedulingType) return;
              onChange({ ...settings, defaultSchedulingType: value });
            }}
            size="small"
            aria-label={t('scheduling.defaultSchedulingType')}
          >
            <ToggleButton value="floating">{t('scheduling.schedulingTypeFloating')}</ToggleButton>
            <ToggleButton value="exact">{t('scheduling.schedulingTypeExact')}</ToggleButton>
          </ToggleButtonGroup>
          <FormHelperText>{t('scheduling.defaultSchedulingTypeHint')}</FormHelperText>
        </Box>
        {/* Step for duration sliders and arrow keys on work-order create (#3091). */}
        <Box sx={{ mb: 3 }}>
          <FormLabel component="legend" sx={{ display: 'block', mb: 1 }}>
            {t('scheduling.workDurationStep')}
          </FormLabel>
          <ToggleButtonGroup
            value={settings.workDurationStepMinutes ?? DEFAULT_WORK_DURATION_STEP_MINUTES}
            exclusive
            onChange={(_e, value: WorkDurationStepMinutes | null) => {
              if (!value) return;
              onChange({ ...settings, workDurationStepMinutes: value });
            }}
            size="small"
            aria-label={t('scheduling.workDurationStep')}
          >
            {WORK_DURATION_STEP_OPTIONS.map((minutes) => (
              <ToggleButton key={minutes} value={minutes}>
                {minutes === 60 ? t('scheduling.workDurationStepHour') : t('scheduling.workDurationStepMinutes', { minutes })}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <FormHelperText>{t('scheduling.workDurationStepHint')}</FormHelperText>
        </Box>
        {/* Three block-level fields capped at 300px used to sit loose in the
            Paper, so on a wide screen they floated side by side at different
            heights with no gutter between them. The Grid is the same one every
            other section of this form uses (#2259). */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('scheduling.lookaheadDays')}
              helperText={t('scheduling.lookaheadDaysHint')}
              value={settings.schedulingLookaheadDays}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 30) {
                  onChange({ ...settings, schedulingLookaheadDays: val });
                }
              }}
              slotProps={{
                htmlInput: { min: 1, max: 30, step: 1 },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('scheduling.lunchMinutes')}
              helperText={t('scheduling.lunchMinutesHint')}
              value={settings.workdayUnpaidLunchMinutes}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 0 && val <= 180) {
                  onChange({ ...settings, workdayUnpaidLunchMinutes: val });
                }
              }}
              slotProps={{
                htmlInput: { min: 0, max: 180, step: 5 },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('scheduling.pickupBufferMinutes')}
              helperText={t('scheduling.pickupBufferMinutesHint')}
              // The cast that used to widen the type here kept this field out
              // of WorkshopSettings, so it was never sent and never loaded —
              // the box showed 60 even for a workshop that had saved 90 (#2618).
              value={settings.defaultPickupBufferMinutes ?? 60}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 0 && val <= 480) {
                  onChange({ ...settings, defaultPickupBufferMinutes: val });
                }
              }}
              slotProps={{
                htmlInput: { min: 0, max: 480, step: 5 },
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
